// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { backendConfig } from '../config/backendConfig';
import PasswordInput from './PasswordInput';
import { authFetch } from '../utils/authFetch';
import { contextAllowsEntitlement, shouldForceStudioOnboarding } from '../utils/studioAccess';

const authInputClass =
  'auth-premium-input w-full p-3 text-sm sm:text-base min-h-[44px]';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const hydrateDeviceLimitError = () => {
      try {
        const msg = localStorage.getItem('auth_device_limit_error');
        if (msg) {
          setError(msg);
          localStorage.removeItem('auth_device_limit_error');
        }
      } catch (_) {}
    };
    const onDeviceLimit = (e) => {
      const msg = e?.detail?.message || 'Login limit reached on this account.';
      setError(msg);
    };
    hydrateDeviceLimitError();
    window.addEventListener('auth-device-limit', onDeviceLimit);
    return () => {
      window.removeEventListener('auth-device-limit', onDeviceLimit);
    };
  }, []);

  const hasValidActiveMembership = (statusData = {}) => {
    const m = statusData?.membership || {};
    const status = (m.status || '').toString().toLowerCase();
    if (!(status === 'active' || status === 'trialing')) return false;
    const endRaw = m.expiresAt || m.currentPeriodEnd || m.trialEndsAt || statusData?.expiresAt;
    if (!endRaw) return true;
    const endDate = typeof endRaw?.toDate === 'function' ? endRaw.toDate() : new Date(endRaw);
    if (Number.isNaN(endDate.getTime())) return true;
    return endDate.getTime() > Date.now();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const credentials = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = credentials?.user || auth.currentUser;

      // Ensure backend session token exists for queue/session-protected APIs.
      if (firebaseUser?.uid) {
        localStorage.setItem('uid', firebaseUser.uid);
        try {
          const idToken = await firebaseUser.getIdToken();
          const loginRes = await fetch(`${backendConfig.baseUrl}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken }),
          });
          if (loginRes.ok) {
            const loginData = await loginRes.json();
            if (loginData?.sessionToken) {
              localStorage.setItem('sessionToken', loginData.sessionToken);
            }
            if (loginData?.user) {
              localStorage.setItem('user', JSON.stringify(loginData.user));
            }
          } else {
            console.warn('Backend session login failed:', loginRes.status);
          }
        } catch (sessionErr) {
          console.warn('Backend session setup failed after Firebase login:', sessionErr);
        }
      }
      
      // Check if user is a mentor and route accordingly (first-time -> setup, returning -> dashboard)
      try {
        const user = auth.currentUser;
        console.log('User logged in:', user?.email);
        
        if (user) {
          const userProfile = await getDoc(doc(db, 'users', user.uid));
          console.log('User profile exists:', userProfile.exists());
          
          if (userProfile.exists()) {
            const userData = userProfile.data();
            console.log('User data:', userData);
            
            // Check membership status for renewal flow - use backend API as source of truth
            // This MUST complete before allowing navigation to other pages
            let shouldRedirectToRenewal = false;
            let membershipStatusSnapshot = null;
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
              
              const statusRes = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}&_t=${Date.now()}`, {
                signal: controller.signal
              });
              clearTimeout(timeoutId);
              
              if (statusRes.ok) {
                const statusData = await statusRes.json();
                membershipStatusSnapshot = statusData;
                const membership = statusData.membership || {};
                const membershipStatus = membership.status || '';
                const isAllowed = statusData.allowed === true;
                const isExpired = membership.isExpired === true;
                const isCanceled = membership.canceled === true;
                
                console.log('Login - Backend membership check:', {
                  status: membershipStatus,
                  allowed: isAllowed,
                  isExpired,
                  isCanceled,
                  membership: membership
                });
                
                // Check if this is a new account (no membership) - should go to membership selection, not renewal
                const isNewAccount = statusData.isNewAccount === true;
                
                // Determine membership intent robustly:
                // - active/trialing with valid period => allow login
                // - new account => membership selection
                // - expired/not active with history => renewal
                if (isNewAccount) {
                  console.log('🆕 New account detected – redirecting to membership selection page');
                  navigate('/membership', { state: { isRenewal: false, userData } });
                  return;
                } else if (hasValidActiveMembership(statusData)) {
                  shouldRedirectToRenewal = false;
                } else if (membershipStatus === 'expired' || isExpired || (!isAllowed && (membershipStatus || membership.currentPeriodEnd || membership.trialEndsAt || membership.expiresAt))) {
                  console.log('🚫 Membership expired or not allowed – redirecting to membership page for renewal');
                  shouldRedirectToRenewal = true;
                }
              } else {
                console.warn('Login - Failed to check membership status from backend; skipping membership redirect fallback');
              }
            } catch (statusError) {
              console.error('Login - Error checking membership status:', statusError);
              // On timeout/abort/network, do not redirect to membership from login flow.
            }
            
            // If expired, redirect immediately and stop further processing
            if (shouldRedirectToRenewal) {
              console.log('🚫 Redirecting expired user to renewal flow');
              navigate('/membership', { state: { isRenewal: true, userData } });
              return; // IMPORTANT: Stop here, don't continue to mentor/regular user routing
            }

            const isMentor =
              userData.mentorSeatsPurchased > 0 ||
              userData.isMentor === true ||
              userData.accountType === 'ngo_admin' ||
              userData.accountType === 'art_mentor';
            if (
              !isMentor &&
              (userData.accountType || 'student') === 'student' &&
              shouldForceStudioOnboarding(membershipStatusSnapshot, userData)
            ) {
              let hasArtistProfile = false;
              try {
                const artistSnap = await getDoc(doc(db, 'artistProfiles', user.uid));
                hasArtistProfile = artistSnap.exists();
              } catch (_) {}
              if (!hasArtistProfile) {
                navigate('/onboarding/studio-access', { replace: true });
                return;
              }
            }

            console.log('Login - User data:', userData);
            console.log('Login - Account type:', userData.accountType);
            console.log('Login - Is mentor:', isMentor);
            
            if (isMentor) {
              // Determine if mentor profile is already created
              let hasMentorProfile = false;
              try {
                const mentorProfileSnap = await getDoc(doc(db, 'mentorProfiles', user.uid));
                hasMentorProfile = mentorProfileSnap.exists();
              } catch (_) {}

              if (hasMentorProfile) {
                console.log('Mentor profile found – navigating to mentor dashboard');
                navigate('/mentor');
              } else {
                console.log('Mentor profile missing – navigating to mentor setup');
                navigate('/mentors/setup');
              }
            } else {
              // Regular user, let AccountTypeRouter handle the routing
              console.log('Login successful, letting AccountTypeRouter handle routing');
              navigate('/');
            }
          } else {
            // No profile exists, check localStorage as fallback
            const accountType = localStorage.getItem('signup_accountType');
            const isMentor = accountType === 'ngo_admin' || accountType === 'art_mentor';
            console.log('No profile, checking localStorage. Account type:', accountType, 'Is mentor:', isMentor);
            
            if (isMentor) {
              navigate('/mentors/setup');
            } else {
              // Let AccountTypeRouter handle the routing
              navigate('/');
            }
          }
        } else {
          console.log('No user found, navigating to home');
          navigate('/');
        }
      } catch (error) {
        console.error('Error checking user profile:', error);
        // Fallback to localStorage check
        const accountType = localStorage.getItem('signup_accountType');
        const isMentor = accountType === 'ngo_admin' || accountType === 'art_mentor';
        console.log('Error occurred, checking localStorage. Account type:', accountType, 'Is mentor:', isMentor);
        
        if (isMentor) {
          navigate('/mentors/setup');
        } else {
          // Let AccountTypeRouter handle the routing
          navigate('/');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up to create an account.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address. Please check your email.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <div className="auth-premium-page">
      <div className="auth-premium-card relative overflow-hidden">
        <div className="auth-premium-gold-rule mb-5" aria-hidden />
        <p className="auth-premium-eyebrow text-center">Sign In</p>
        <h1 className="auth-premium-title mb-6 text-center text-3xl">Welcome Back</h1>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="auth-premium-label mb-1.5 block text-sm">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              placeholder="your.email@example.com"
              className={authInputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="login-password" className="auth-premium-label mb-1.5 block text-sm">
              Password
            </label>
            <PasswordInput
              id="login-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={authInputClass}
            />
            <div className="mt-2 text-right">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="auth-premium-link text-sm"
              >
                Forgot password?
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="art-btn-primary w-full rounded-xl p-3 font-semibold transition-all duration-300 transform hover:scale-[1.02]"
          >
            Log In
          </button>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <p className="auth-premium-subtitle mt-4 text-center text-sm">
            Don&apos;t have an account?{' '}
            <button type="button" onClick={() => navigate('/signup')} className="auth-premium-link">
              Join our community
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}